import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import CouponFormModal from '../../components/admin/CouponFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import './Admin.css';

interface Coupon {
  id: number;
  code: string;
  discount_type: string;
  discount_type_display: string;
  discount_value: number;
  min_purchase_amount: number;
  usage_limit: number | null;
  used_count: number;
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  is_valid: boolean;
}

const CouponManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  const columnDefs: ColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      flex: 0.5, 
      minWidth: 60, 
      sortable: true,
      cellStyle: { color: '#6b7280', fontWeight: '500' }
    },
    { 
      field: 'code', 
      headerName: 'Code', 
      sortable: true, 
      filter: true, 
      flex: 1.5, 
      minWidth: 120,
      cellStyle: { fontWeight: '600', color: '#1f2937' }
    },
    { 
      field: 'discount_type_display', 
      headerName: 'Type', 
      flex: 1, 
      minWidth: 100,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value === 'Percentage' ? '#ede9fe' : '#fef3c7',
          color: params.value === 'Percentage' ? '#7c3aed' : '#d97706',
          padding: '4px 10px',
          borderRadius: '12px',
          fontWeight: '600',
          fontSize: '12px'
        }}>
          {params.value}
        </span>
      )
    },
    { 
      field: 'discount_value', 
      headerName: 'Value', 
      flex: 0.8,
      minWidth: 80,
      cellRenderer: (params: any) => {
        const type = params.data.discount_type;
        const isPercentage = type === 'percentage';
        return (
          <span style={{
            color: isPercentage ? '#7c3aed' : '#10b981',
            fontWeight: '800',
            fontSize: '14px'
          }}>
            {isPercentage ? `${params.value}%` : `৳${params.value}`}
          </span>
        );
      }
    },
    { 
      field: 'min_purchase_amount', 
      headerName: 'Min Purchase', 
      flex: 1,
      minWidth: 100,
      cellStyle: { fontWeight: '600', color: '#10b981' },
      valueFormatter: (params) => `৳${params.value}`
    },
    { 
      field: 'used_count', 
      headerName: 'Used', 
      flex: 0.6,
      minWidth: 70,
      cellRenderer: (params: any) => {
        const limit = params.data.usage_limit;
        const used = params.value;
        const isNearLimit = limit && used >= limit * 0.8;
        return (
          <span style={{
            color: isNearLimit ? '#f59e0b' : '#6b7280',
            fontWeight: '600'
          }}>
            {limit ? `${used}/${limit}` : used}
          </span>
        );
      }
    },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      flex: 0.8,
      minWidth: 90,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value ? '#d1fae5' : '#fee2e2',
          color: params.value ? '#059669' : '#dc2626',
          padding: '4px 12px',
          borderRadius: '12px',
          fontWeight: '600',
          fontSize: '12px'
        }}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit Coupon"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete Coupon"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCoupons();
      const data = response.data || response;
      const couponsList = Array.isArray(data) ? data : (data.results || data.data || []);
      setCoupons(couponsList);
    } catch (error) {
      console.error('Failed to load coupons:', error);
      setToast({ show: true, message: 'Failed to load coupons', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCoupon) return;
    
    try {
      await adminAPI.deleteCoupon(selectedCoupon.id);
      setToast({ show: true, message: 'Coupon deleted successfully', type: 'success' });
      loadCoupons();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete coupon', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedCoupon(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedCoupon) {
        await adminAPI.updateCoupon(selectedCoupon.id, formData);
        setToast({ show: true, message: 'Coupon updated successfully', type: 'success' });
      } else {
        await adminAPI.createCoupon(formData);
        setToast({ show: true, message: 'Coupon created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedCoupon(null);
      loadCoupons();
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedCoupon(null);
    setIsModalOpen(true);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure coupons is always an array before filtering
  const couponsArray = Array.isArray(coupons) ? coupons : [];
  const filteredCoupons = couponsArray.filter(coupon =>
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Coupon Management</h3>
          <p>Create and manage discount coupons</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add New Coupon
          </button>
          <button className="btn btn-secondary" onClick={loadCoupons}>
            🔄 Refresh
          </button>
        </div>
      </div>
      
      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search coupons by code..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredCoupons.length}</span> coupons found
        </div>
      
        <div className="ag-grid-container">
          <AgGridReact
            theme={themeQuartz}
            rowData={filteredCoupons || []}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
            }}
            rowHeight={65}
            headerHeight={48}
            animateRows={true}
            rowSelection="single"
            suppressCellFocus={true}
          />
        </div>
      </div>

      <CouponFormModal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setSelectedCoupon(null); 
        }}
        onSave={handleSave}
        coupon={selectedCoupon}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedCoupon(null);
        }}
        onConfirm={handleDelete}
        title="Delete Coupon"
        message={`Are you sure you want to delete coupon "${selectedCoupon?.code}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default CouponManagement;
